import { compact, filter, keyBy, map, orderBy } from "lodash-es";

import {
  type AssistantFollowUpObservable,
  type AssistantProjectObservable,
  type AssistantTaskObservable,
} from "@/store/assistant";
import { getAgentTimestamp } from "@/pages/agent/utils/agent-date.utils";
import { isClosedFollowUp } from "@/pages/agent/utils/agent-follow-up.utils";
import { isClosedAssistantTaskStatus } from "@/store/assistant/assistant-task-status";

export type ProjectDetailViewModel = {
  project: AssistantProjectObservable;
  openTasksCount: number;
  nextFollowUp: AssistantFollowUpObservable | null;
  followUpCount: number;
};

/**
 * Joins a project with its tasks and the follow-ups that belong to it.
 *
 * Follow-ups can attach to a project two ways: pointing directly at the
 * project, or pointing at one of its tasks. Both forms are collected here so
 * the rendering layer doesn't need to know which is which.
 */
export const buildProjectDetailViewModel = ({
  project,
  tasks,
  followUps,
}: {
  project: AssistantProjectObservable;
  tasks: AssistantTaskObservable[];
  followUps: AssistantFollowUpObservable[];
}): ProjectDetailViewModel => {
  const projectTasks = filter(tasks, task => task.projectId === project.id);
  const openTasks = filter(
    projectTasks,
    task => !isClosedAssistantTaskStatus({ status: task.status })
  );
  const tasksById = keyBy(projectTasks, task => task.id);

  const projectFollowUps = compact(
    map(followUps, followUp => {
      if (followUp.projectId === project.id) {
        return followUp;
      }

      if (followUp.taskId && tasksById[followUp.taskId]) {
        return followUp;
      }

      return null;
    })
  );

  const openFollowUps = filter(projectFollowUps, followUp => !isClosedFollowUp({ followUp }));
  const nextFollowUp =
    orderBy(
      openFollowUps,
      followUp =>
        getAgentTimestamp({
          value: followUp.scheduledFor ?? followUp.dueAt,
          fallback: Number.POSITIVE_INFINITY,
        }),
      "asc"
    )[0] ?? null;

  return {
    project,
    openTasksCount: openTasks.length,
    nextFollowUp,
    followUpCount: projectFollowUps.length,
  };
};
